import React, { useState, useEffect, useCallback, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import RefreshIcon from "@material-ui/icons/Refresh";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";

// Tela de status do monitor de SEFAZ, criada 2026-09-08 depois de um
// timeout real de NFC-e (08/09/2026 10:17:01) que o sistema nao mostrou
// em lugar nenhum: o unico aviso era uma notificacao de socket ao vivo,
// so' pra admin, e o unico registro era o estado ATUAL - se ninguem
// estivesse com o sistema aberto na hora, o problema desaparecia sem
// deixar rastro.
//
// De proposito acessivel a QUALQUER perfil (o item de menu nao tem
// <Can>, e GET /settings-sefaz/monitor tambem nao tem guard de admin):
// quem lida com emissao nao necessariamente e' admin.
const REFRESH_INTERVAL_MS = 60000;

const DOC_TYPES = [
  { key: "nfce", label: "NFC-e" },
  { key: "nfe", label: "NF-e" }
];

// As duas fontes veem coisas diferentes, e a distincao importa na hora
// de agir: "certificado" caindo pode ser problema NOSSO (certificado
// vencido da um 403); "envio" caindo e' problema do SEFAZ e nao tem o
// que fazer alem de esperar.
// staleAfterMinutes: cada fonte roda num cron de intervalo diferente
// (certificado a cada 10min, tecnospeed a cada 2min) - o limiar de
// "desatualizado" e' ~2-3x o proprio intervalo, pra nao acusar atraso
// por uma unica falha isolada, mas pegar rapido quando o monitor
// externo fica fora do ar por mais tempo (ver CheckSefazEnvioMonitorService:
// numa falha de leitura, o estado simplesmente nao e' atualizado, entao
// sem isso a tela mostra o ultimo status bom indefinidamente).
const SOURCES = [
  {
    key: "certificado",
    label: "Conexão e certificado",
    staleAfterMinutes: 25,
    help:
      "Nossa própria checagem, com o certificado digital da empresa, " +
      "contra o serviço de status do SEFAZ. Um erro aqui pode ser " +
      "problema nosso — certificado vencido, senha errada ou não autorizado."
  },
  {
    key: "tecnospeed",
    label: "Envio / autorização",
    staleAfterMinutes: 6,
    help:
      "Monitor externo (Tecnospeed) do serviço de autorização, que é o " +
      "que realmente emite a nota. É um webservice diferente do de " +
      "status: o SEFAZ costuma responder status normal com o envio fora."
  }
];

const isStale = (lastCheckAt, staleAfterMinutes) =>
  !!lastCheckAt && moment().diff(moment(lastCheckAt), "minutes") > staleAfterMinutes;

const STATUS_LABEL = {
  normal: "Normal",
  lento: "Lento",
  muito_lento: "Muito lento",
  timeout: "Timeout",
  erro: "Erro"
};

const STATUS_COLOR = {
  normal: "#22c55e",
  lento: "#eab308",
  muito_lento: "#f59e0b",
  timeout: "#ef4444",
  erro: "#ef4444"
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll", overflowX: "auto",
    ...theme.scrollbarStyles
  },
  docCard: {
    padding: theme.spacing(1.5),
    height: "100%"
  },
  sourceRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: "1px solid",
    borderColor: theme.palette.divider
  },
  sourceLabel: {
    minWidth: 0
  },
  muted: {
    color: theme.palette.text.secondary
  },
  staleWarning: {
    color: "#b45309",
    fontWeight: 600
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  message: {
    fontSize: 13,
    wordBreak: "break-word"
  }
}));

const StatusChip = ({ status }) => {
  if (!status) {
    return <Chip size="small" label="Sem dado" style={{ height: 20, fontSize: 11 }} />;
  }
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[status] || status}
      style={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        color: "#fff",
        backgroundColor: STATUS_COLOR[status] || "#9e9e9e"
      }}
    />
  );
};

const SefazMonitor = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [current, setCurrent] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMonitor = useCallback(async (silent) => {
    try {
      const { data } = await api.get("/settings-sefaz/monitor");
      setCurrent(data?.current || {});
      setEvents(data?.events || []);
    } catch (err) {
      // O ciclo automatico falha em silencio (nao adianta encher a tela
      // de toast a cada minuto); o botao de atualizar avisa.
      if (!silent) toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitor(true);
    const interval = setInterval(() => fetchMonitor(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMonitor]);

  // Alerta novo chegando por socket atualiza a tela na hora, sem
  // esperar o ciclo - mesmo evento que o sino de notificacoes ouve.
  useEffect(() => {
    if (!socket || !user?.companyId) return undefined;
    const eventName = `company-${user.companyId}-sefaz-alert`;
    const onAlert = () => fetchMonitor(true);
    socket.on(eventName, onAlert);
    return () => socket.off(eventName, onAlert);
  }, [socket, user, fetchMonitor]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Monitor SEFAZ</Title>
        <Tooltip title="Atualizar agora">
          <IconButton onClick={() => fetchMonitor(false)}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2}>
          {DOC_TYPES.map(doc => (
            <Grid item xs={12} md={6} key={doc.key}>
              <Paper className={classes.docCard} variant="outlined">
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  {doc.label}
                </Typography>
                {SOURCES.map(source => {
                  const state = current?.[doc.key]?.[source.key];
                  const stale = isStale(state?.lastCheckAt, source.staleAfterMinutes);
                  return (
                    <div className={classes.sourceRow} key={source.key}>
                      <div className={classes.sourceLabel}>
                        <Tooltip title={source.help}>
                          <Typography variant="body2">{source.label}</Typography>
                        </Tooltip>
                        {state?.lastCheckAt && (
                          <Typography
                            variant="caption"
                            className={stale ? classes.staleWarning : classes.muted}
                          >
                            {stale ? "⚠️ " : ""}
                            verificado {moment(state.lastCheckAt).format("HH:mm:ss")}
                            {stale ? " — sem confirmação recente, pode estar desatualizado" : ""}
                          </Typography>
                        )}
                      </div>
                      <StatusChip status={stale ? null : state?.status} />
                    </div>
                  );
                })}
                {!current?.[doc.key] && (
                  <Typography variant="caption" className={classes.muted}>
                    Nenhuma checagem registrada ainda.
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" className={classes.sectionTitle}>
          Histórico de eventos
        </Typography>
        <Typography variant="body2" className={classes.muted} style={{ marginBottom: 8 }}>
          Todo problema detectado fica registrado aqui, mesmo que já tenha
          se normalizado e mesmo que ninguém estivesse com o sistema aberto
          na hora.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Mensagem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" className={classes.muted}>
                    Nenhum evento registrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {events.map(event => (
              <TableRow key={event.id}>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  {moment(event.occurredAt).format("DD/MM/YYYY HH:mm:ss")}
                </TableCell>
                <TableCell>{event.docType === "nfce" ? "NFC-e" : "NF-e"}</TableCell>
                <TableCell>
                  {event.source === "tecnospeed" ? "Envio / autorização" : "Conexão e certificado"}
                </TableCell>
                <TableCell>
                  <StatusChip status={event.status} />
                </TableCell>
                <TableCell className={classes.message}>{event.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default SefazMonitor;
